<template>
  <div class="app-shell">
    <!-- 手機版左上：側欄開關 -->
    <button
      class="menu-toggle"
      :class="{ open: isMobileNavOpen }"
      type="button"
      :aria-label="isMobileNavOpen ? '關閉選單' : '開啟選單'"
      :aria-expanded="isMobileNavOpen ? 'true' : 'false'"
      @click="toggleNav"
    >
      <!-- chevron_right / chevron_left -->
      <svg
        class="chevron"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <!-- 收合：向右 -->
        <path
          v-if="!isMobileNavOpen"
          d="M9 6l6 6-6 6"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <!-- 展開：向左 -->
        <path
          v-else
          d="M15 6l-6 6 6 6"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>


    <!-- 手機版：開啟側欄時的遮罩 -->
    <div v-if="isMobileNavOpen" class="backdrop" @click="closeNav" />

    <aside :class="['sidebar', { open: isMobileNavOpen, collapsed: isDesktopCollapsed }]" id="mobile-sidebar">
      <!-- 電腦版縮收按鈕 -->
      <button
        class="desktop-toggle"
        type="button"
        @click="isDesktopCollapsed = !isDesktopCollapsed"
        :aria-label="isDesktopCollapsed ? '展開側欄' : '收合側欄'"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path
            v-if="isDesktopCollapsed"
            d="M13 5l7 7-7 7M5 5l7 7-7 7"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            v-else
            d="M11 19l-7-7 7-7M19 19l-7-7 7-7"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <nav class="nav-container" ref="navRef" @click="closeNavOnNavigate">
        <!-- 滑動背景 -->
        <div class="nav-glider" :style="gliderStyle"></div>

        <router-link
          v-for="item in visibleNavItems"
          :key="item.key"
          :class="['nav-item', { active: isNavItemActive(item) }]"
          :to="{ name: item.routeName }"
          :aria-label="$t(item.i18nKey)"
          @click.prevent="onClickNav(item)"
        >
          <span class="nav-icon" v-if="item.icon" v-html="item.icon"></span>
          <span class="nav-text">{{ $t(item.i18nKey) }}</span>
        </router-link>
      </nav>
      <!-- <button class="logout" @click="onSignOut">{{ $t('common.logout') }}</button> -->
    </aside>

    <main class="content">
      <router-view />
    </main>

    <!-- ★ 身分驗證提示彈窗（未驗證） -->
    <div v-if="showVerifyModal" class="modal-backdrop" @click.self="closeVerifyModal">
      <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="verify-title">
        <h3 id="verify-title" class="modal-title">{{ $t('verifyModal.title') }}</h3>
        <p class="modal-text">
          {{ $t('verifyModal.line1') }}<br>
          {{ $t('verifyModal.line2') }}<br>
          <a class="inline-link" :href="termsHref" target="_blank" rel="noopener">
            {{ $t('verifyModal.linkPrefix') }}{{ $t('member.Privacy') }}{{ $t('verifyModal.linkSuffix') }}
          </a>
        </p>
        <div class="modal-actions">
          <button class="btn btn-primary" @click="confirmAndGoProfile">
            {{ $t('verifyModal.confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ★ 公告輪播彈窗（已驗證；同分頁只顯示一次） -->
    <div v-if="showAnnouncementModal" class="modal-backdrop" @click.self="closeAnnouncement">
      <div
        class="announcement-card"
        :class="{ 'has-banner': bannerSlide }"
        role="dialog"
        aria-modal="true"
        aria-label="當期活動公告"
        @mouseenter="pauseSlides"
        @mouseleave="resumeSlides"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <button class="announcement-close" aria-label="關閉公告" @click="closeAnnouncement">×</button>

        <!-- ✅ 有 bannerSlide：在輪播上方多顯示橫幅 -->
        <div
          v-if="bannerSlide"
          class="banner-wrapper"
          @click="onBannerClick"
        >
          <component
            :is="bannerHref ? 'a' : 'div'"
            :href="bannerHref || null"
            class="banner-slide"
          >
            <img
              class="banner-img"
              :src="bannerSlide.image_url"
              :alt="bannerSlide.title || 'TSK 活動橫幅'"
            />
          </component>
        </div>

        <!-- 🎠 輪播：只有在有非 banner 活動時顯示 -->
        <div
          v-if="promoSlides.length"
          class="carousel"
          @click="onCarouselClick"
        >
          <component
            :is="currentSlideHref ? 'a' : 'div'"
            :href="currentSlideHref || null"
            class="carousel-slide"
          >
            <img
              class="carousel-img"
              :src="currentSlide.image_url"
              :alt="currentSlide.title || 'TSK 當期活動'"
            />
          </component>

          <button
            v-if="promoSlides.length > 1"
            class="nav prev"
            @click="prevSlide"
            aria-label="上一張"
          >‹</button>

          <button
            v-if="promoSlides.length > 1"
            class="nav next"
            @click="nextSlide"
            aria-label="下一張"
          >›</button>

          <div v-if="promoSlides.length > 1" class="dots">
            <button
              v-for="(s, idx) in promoSlides"
              :key="s.id || idx"
              class="dot"
              :class="{ active: idx === slideIndex }"
              @click="goSlide(idx)"
              :aria-label="`第 ${idx+1} 張`"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'
import { appShellNavItems } from '@/config/AppShellNav'

export default {
  name: 'AppShell',
  data() {
    return {
      isMobileNavOpen: false,
      isDesktopCollapsed: false,
      showVerifyModal: false,
      verifyModalReason: 'enter-app',
      lastKnownIdentityStatus: null,
      fetchingStatus: false,

      // ★ 是否有當期活動（決定是否顯示選單）
      hasActivePromo: false,

      // ★ 公告輪播
      showAnnouncementModal: false,
      promoSlides: [],         // [{ id,title,image_url,link_url }]
      slideIndex: 0,
      slideTimer: null,
      slideAutoplayMs: 3500,

      bannerSlide: null,

      // 觸控滑動
      touchStartX: 0,
      touchDeltaX: 0,

      // 滑動背景樣式
      gliderStyle: {
        top: '0px',
        height: '0px',
        opacity: 0
      }
    }
  },

  async beforeRouteEnter(to, from, next) {
    const { data } = await supabase.auth.getSession()
    if (!data?.session) next('/member')
    else next()
  },

  async mounted() {
    window.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)

    await this.refreshActivePromoFlag()   // 是否有當期活動
    await this.checkIdentityOnEnter()     // 進殼層判斷是否彈窗
    await this.checkIdentityOnRoute(this.$route)
    this.$nextTick(() => this.updateGlider())
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    document.body.style.overflow = ''
    this.clearSlideTimer()
  },

  watch: {
    $route(to) {
      this.closeNav()
      this.checkIdentityOnRoute(to)
      this.$nextTick(() => this.updateGlider())
    },
    hasActivePromo() {
      this.$nextTick(() => this.updateGlider())
    },
    isMobileNavOpen() { this.updateBodyScrollLock() },
    showVerifyModal() { this.updateBodyScrollLock() },
    showAnnouncementModal(val) {
      this.updateBodyScrollLock()
      if (val) {
        this.startSlides()
      } else {
        this.clearSlideTimer()
      }
    },
  },

  computed: {
    visibleNavItems() {
      return appShellNavItems.filter((item) => {
        if (typeof item.show === 'function') {
          // show(ctx) → ctx 是 this
          return item.show(this)
        }
        return true
      })
    },
    
    currentSlide() {
      if (!this.promoSlides.length) {
        return { image_url: '', link_url: '', title: '' }
      }
      return this.promoSlides[this.slideIndex]
    },
    termsHref() {
      return this.$router.resolve({ name: 'TermsPrivacy' }).href
    },
    // ✅ 目前輪播那張要開的新視窗網址
    currentSlideHref() {
      return this.buildPromoDetailHref(this.currentSlide)
    },

    // ✅ banner 要開的新視窗網址
    bannerHref() {
      return this.buildPromoDetailHref(this.bannerSlide)
    }
  },

  methods: {
    // ====== 新版：統一選單點擊處理 ======
    async onClickNav(item) {
      // 如果有 gatedReason，就走驗證流程
      if (item.gatedReason) {
        const ok = await this.ensureVerifiedOrPrompt(item.gatedReason)
        if (!ok) return
      }

      // 再導到對應頁面
      this.$router.push({ name: item.routeName })
    },

    // 給 glider & active 樣式用
    isNavItemActive(item) {
      const path = this.$route.path
      if (Array.isArray(item.activePrefixes)) {
        return item.activePrefixes.some((prefix) => path.startsWith(prefix))
      }
      return false
    },
    
    // ====== 左側「當期活動」選單：是否顯示 ======
    async refreshActivePromoFlag() {
      // 由於尚未建立 promotions 資料表，先將其改為 false，避免報 404
      this.hasActivePromo = false
    },

    // ====== 當期活動入口攔截 ======
    async onClickPromo() {
      const ok = await this.ensureVerifiedOrPrompt('promo')
      if (ok) this.$router.push({ name: 'PromoList' })
    },

    // ====== 產品總覽入口攔截 ======
    async onClickProducts() {
      const ok = await this.ensureVerifiedOrPrompt('products')
      if (ok) this.$router.push({ name: 'AppProducts' })
    },

    // ====== 精選分享入口攔截 ======
    async onClickPapers () {
      const ok = await this.ensureVerifiedOrPrompt('papers')
      if (ok) {
        this.$router.push({ name: 'AppPapers' })
      }
    },

    // ====== 影片入口攔截 ======
    async onClickVideos() {
      const ok = await this.ensureVerifiedOrPrompt('video')
      if (ok) this.$router.push({ name: 'VideoIndex' })
    },

    // ⭐ 新增：醫師分享入口攔截
    async onClickShare() {
      const ok = await this.ensureVerifiedOrPrompt('share')
      if (ok) this.$router.push({ name: 'AppShare' })
    },

    // ⭐ 新增：下載專區入口攔截
    async onClickDownload() {
      const ok = await this.ensureVerifiedOrPrompt('download')
      if (ok) this.$router.push({ name: 'AppDownload' })
    },

    async ensureVerifiedOrPrompt(reason = 'video') {
      try {
        const status = await this.fetchIdentityStatus()
        if (status === 'verified') return true
        this.verifyModalReason = reason
        this.showVerifyModal = true
        return false
      } catch (e) {
        this.verifyModalReason = reason
        this.showVerifyModal = true
        return false
      }
    },

    async fetchIdentityStatus() {
      if (this.fetchingStatus) return this.lastKnownIdentityStatus
      try {
        this.fetchingStatus = true
        const { data: sess } = await supabase.auth.getSession()
        const uid = sess?.session?.user?.id
        if (!uid) return null

        const { data, error } = await supabase
          .from('user_profiles')
          .select('identity_status')
          .eq('id', uid)
          .maybeSingle()

        if (error) {
          console.debug('[fetchIdentityStatus] error', error.message)
          return this.lastKnownIdentityStatus
        }

        const status = (data?.identity_status || 'unverified').toLowerCase()
        this.lastKnownIdentityStatus = status
        return status
      } finally {
        this.fetchingStatus = false
      }
    },

    // ====== 進入殼層時的提示邏輯 ======
    async checkIdentityOnEnter() {
      try {
        const status = await this.fetchIdentityStatus()
        const onProfile = this.$route.name === 'AppProfile'

        // 未驗證：同分頁只提醒一次
        if (status !== 'verified' && !onProfile) {
          if (sessionStorage.getItem('id_prompt_shown') !== '1') {
            this.verifyModalReason = 'enter-app'
            this.showVerifyModal = true
            sessionStorage.setItem('id_prompt_shown', '1')
          }
          return
        }

        // 已驗證：同分頁只顯示一次公告
        if (status === 'verified') {
          if (sessionStorage.getItem('announcement_shown') !== '1') {
            await this.loadPromoSlides()
            if (this.promoSlides.length) {
              this.slideIndex = 0
              this.showAnnouncementModal = true
              sessionStorage.setItem('announcement_shown', '1')
            }
          }
        }
      } catch (e) {
        console.debug('[checkIdentityOnEnter] skipped', e?.message || e)
      }
    },

    // 抓取當期活動的輪播素材
    async loadPromoSlides() {
      // 由於尚未建立 promotions 資料表，先將其清除，避免報 404
      this.promoSlides = []
      this.bannerSlide = null
    },

    onCarouselClick(e) {
      if (e.target.closest('.nav') || e.target.closest('.dot')) return

      const go = () => {
        this.closeAnnouncement()
        const promoId = this.currentSlide.id
        this.$router.push({ name: 'PromoList', query: { promo: promoId } })
      }

      this.ensureVerifiedOrPrompt('promo').then(ok => {
        if (ok) go()
      })
    },

    onBannerClick(e) {
      // 如果點到的是 <a> 裡的東西就讓瀏覽器自己處理外連
      if (e.target.closest('a')) return

      const go = () => {
        this.closeAnnouncement()
        const promoId = this.bannerSlide?.id
        if (promoId) {
          this.$router.push({ name: 'PromoList', query: { promo: promoId } })
        } else {
          this.$router.push({ name: 'PromoList' })
        }
      }

      this.ensureVerifiedOrPrompt('promo').then(ok => {
        if (ok) go()
      })
    },

    buildPromoDetailHref(slide) {
      if (!slide) return null

      const raw = (slide.link_url || '').trim()

      // 1. 外部連結，就走外部連結
      if (raw && /^https?:\/\//i.test(raw)) {
        return raw
      }

      // 2. 內部路徑 → 萃取最後一段 folder
      let folder = ''
      if (raw) {
        const stripped = raw.replace(/^\/+|\/+$/g, '')
        const parts = stripped.split('/')
        folder = parts[parts.length - 1]
      }

      // 3. 若沒有 folder，就保持原本行為（項目閃動，不跳 detail）
      if (!folder) return null

      // 4. 組 promo/detail/:folder
      return this.$router.resolve({
        name: 'PromoDetail',
        params: { folder }
      }).href
    },


    // ====== 切頁時：需要驗證的頁面再提醒 ======
    async checkIdentityOnRoute(to) {
      try {
        const name = to?.name
        // 需要驗證的頁面：影片 + 當期活動 + 產品總覽 + 精選分享 + 醫師分享 + 下載專區
        const gated = [
          'VideoIndex', 'TopicVideos', 'VideoSubtopic', 'VideoPlay',
          'PromoList', 'AppProducts', 'AppPapers',
          'AppShare', 'AppDownload','AppTrial',
        ]
        if (!gated.includes(name)) return

        const status = await this.fetchIdentityStatus()
        const onProfile = name === 'AppProfile'
        if (status !== 'verified' && !onProfile) {
          if (name === 'PromoList')           this.verifyModalReason = 'promo'
          else if (name === 'AppProducts')    this.verifyModalReason = 'products'
          else if (name === 'AppPapers')      this.verifyModalReason = 'papers'
          else if (name === 'AppShare')       this.verifyModalReason = 'share'
          else if (name === 'AppDownload')    this.verifyModalReason = 'download'
          else if (name === 'AppTrial')       this.verifyModalReason = 'trial'
          else                                 this.verifyModalReason = 'video'

          this.showVerifyModal = true
        }
      } catch (e) {
        console.debug('[checkIdentityOnRoute] skipped', e?.message || e)
      }
    },

    // ====== 公告 modal 控制 ======
    closeAnnouncement() { this.showAnnouncementModal = false },

    // ====== 輪播控制 ======
    startSlides() {
      this.clearSlideTimer()
      if (this.promoSlides.length <= 1) return
      this.slideTimer = setInterval(this.nextSlide, this.slideAutoplayMs)
    },
    clearSlideTimer() {
      if (this.slideTimer) {
        clearInterval(this.slideTimer)
        this.slideTimer = null
      }
    },
    pauseSlides() { this.clearSlideTimer() },
    resumeSlides() { if (this.showAnnouncementModal) this.startSlides() },
    nextSlide() {
      if (!this.promoSlides.length) return
      this.slideIndex = (this.slideIndex + 1) % this.promoSlides.length
    },
    prevSlide() {
      if (!this.promoSlides.length) return
      this.slideIndex = (this.slideIndex - 1 + this.promoSlides.length) % this.promoSlides.length
    },
    goSlide(idx) {
      if (idx >= 0 && idx < this.promoSlides.length) this.slideIndex = idx
    },

    // 觸控滑動（手機）
    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchDeltaX = 0
      this.pauseSlides()
    },
    onTouchMove(e) {
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX
    },
    onTouchEnd() {
      const threshold = 40
      if (this.touchDeltaX > threshold) this.prevSlide()
      else if (this.touchDeltaX < -threshold) this.nextSlide()
      this.resumeSlides()
      this.touchStartX = 0
      this.touchDeltaX = 0
    },

    // ====== 會員驗證 modal 控制 ======
    closeVerifyModal() { this.showVerifyModal = false },
    confirmAndGoProfile() {
      this.showVerifyModal = false
      if (this.$route.name !== 'AppProfile') {
        this.$router.replace({ name: 'AppProfile' })
      }
    },

    // ====== 其餘 ======
    async onSignOut() {
      await supabase.auth.signOut()
      this.$router.replace('/member')
    },

    // 側欄（手機）
    toggleNav() { this.isMobileNavOpen = !this.isMobileNavOpen },
    closeNav() { if (this.isMobileNavOpen) this.isMobileNavOpen = false },
    closeNavOnNavigate(e) { if (e.target && e.target.closest('a')) this.closeNav() },

    // 共用：鎖定 body 滾動
    updateBodyScrollLock() {
      const anyOpen = this.isMobileNavOpen || this.showVerifyModal || this.showAnnouncementModal
      document.body.style.overflow = anyOpen ? 'hidden' : ''
    },

    // 鍵盤關閉 / 切換
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (this.showVerifyModal) this.closeVerifyModal()
        else if (this.showAnnouncementModal) this.closeAnnouncement()
        else this.closeNav()
      }
      if (this.showAnnouncementModal && this.promoSlides.length > 1) {
        if (e.key === 'ArrowLeft') this.prevSlide()
        if (e.key === 'ArrowRight') this.nextSlide()
      }
    },

    // 視窗放大自動關閉抽屜
    onResize() {
      if (window.innerWidth >= 900) this.closeNav()
      this.updateGlider()
    },

    // 更新滑動背景位置
    updateGlider() {
      const nav = this.$refs.navRef
      if (!nav) return

      // 因為 active class 是動態綁定的，DOM 更新可能需要一點時間
      // 這裡假設 updateGlider 是在 $nextTick 呼叫的，所以應該抓得到
      const activeLink = nav.querySelector('.nav-item.active')
      if (activeLink) {
        const navRect = nav.getBoundingClientRect()
        const linkRect = activeLink.getBoundingClientRect()

        const top = linkRect.top - navRect.top
        const height = linkRect.height

        this.gliderStyle = {
          top: `${top}px`,
          height: `${height}px`,
          opacity: 1
        }
      } else {
        this.gliderStyle = {
          ...this.gliderStyle,
          opacity: 0
        }
      }
    },

    // 判斷導航是否啟用（包含子路徑）
    isNavActive(section) {
      const path = this.$route.path
      switch (section) {
        case 'promo':
          return path.startsWith('/app/promo')
        case 'products':
          return path.startsWith('/app/products') || path.startsWith('/app/product/')
        case 'papers':
          return path.startsWith('/app/papers') || path.startsWith('/app/paper/')
        case 'videos':
          return path.startsWith('/app/videos') || path.startsWith('/app/video/')
        case 'share':
          return path.startsWith('/app/share')
        case 'download':
          return path.startsWith('/app/download')
        case 'profile':
          return path.startsWith('/app/profile')
        default:
          return false
      }
    }
  }
}
</script>

<style scoped>
/* =========================================================
   AppShell – Deep Purple Glassmorphism Theme
   ========================================================= */

.app-shell {
  --app-top-offset: var(--header-height, 0px);
  display: flex;
  margin-top: var(--app-top-offset);
  min-height: calc(100vh - var(--app-top-offset));
  box-sizing: border-box;

  /* 🌌 背景色系 */
  --bg-0: #070214;
  --bg-1: #12052a;
  --bg-2: #1a0838;

  /* 🎨 主色 */
  --accent: #a78bfa;
  --accent-2: #60a5fa;

  /* 🧾 文字 */
  --text: rgba(255,255,255,.92);
  --muted: rgba(255,255,255,.68);

  /* 🧊 玻璃 */
  --glass: rgba(255,255,255,.10);
  --glass-weak: rgba(255,255,255,.06);
  --glass-border: rgba(255,255,255,.18);

  --shadow: 0 18px 45px rgba(0,0,0,.45);

  background:
    radial-gradient(900px 520px at 15% 10%, rgba(167,139,250,.22), transparent 55%),
    radial-gradient(900px 520px at 85% 25%, rgba(96,165,250,.18), transparent 55%),
    linear-gradient(140deg, var(--bg-0), var(--bg-1) 45%, var(--bg-2));
  color: var(--text);
}

/* =========================================================
   Sidebar
   ========================================================= */

.sidebar {
  --sidebar-width: 260px;
  width: var(--sidebar-width);
  position: sticky;
  top: 60px;
  height: calc(100vh - 60px);

  padding: calc(var(--app-top-offset) + 16px) 16px 16px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  background: linear-gradient(180deg, var(--glass), var(--glass-weak));
  border-right: 1px solid var(--glass-border);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);

  box-shadow: 12px 0 30px rgba(0,0,0,.28);
  transition: width .3s cubic-bezier(.4, 0, .2, 1);
  overflow-x: hidden;
}

.sidebar.collapsed {
  --sidebar-width: 80px;
}

/* 電腦版收合按鈕 */
.desktop-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-bottom: 20px;
  margin-left: auto;
  border-radius: 8px;
  border: 1px solid var(--glass-border);
  background: var(--glass-weak);
  color: var(--muted);
  cursor: pointer;
  transition: all .2s;
}

.sidebar.collapsed .desktop-toggle {
  margin-left: auto;
  margin-right: auto;
}

.desktop-toggle:hover {
  background: var(--glass);
  color: var(--text);
  border-color: var(--accent);
}

.nav-container {
  position: relative;
}

/* 滑動高亮（玻璃紫藍） */
.nav-glider {
  position: absolute;
  left: 0;
  right: 0;
  border-radius: 12px;
  pointer-events: none;
  z-index: 0;

  background: linear-gradient(
    135deg,
    rgba(167,139,250,.42),
    rgba(96,165,250,.30)
  );
  border: 1px solid rgba(255,255,255,.22);
  box-shadow: 0 10px 26px rgba(0,0,0,.30);

  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  transition: all .3s cubic-bezier(.25,.8,.25,1);
}

/* 導航項目 */
.nav-item {
  position: relative;
  z-index: 1;

  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border-radius: 12px;
  text-decoration: none;

  color: var(--muted);
  transition: color .2s, background .2s, transform .15s ease, padding .3s;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 10px 0;
  gap: 0;
}

.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.nav-item:hover {
  color: var(--text);
  background: rgba(255,255,255,.06);
  transform: translateY(-1px);
}

.nav-item.active {
  color: #fff;
  background: transparent;
}

.nav-text {
  position: relative;
  z-index: 2;
  white-space: nowrap;
  transition: opacity .3s, transform .3s;
}

.sidebar.collapsed .nav-text {
  opacity: 0;
  width: 0;
  overflow: hidden;
  pointer-events: none;
  transform: translateX(-10px);
}

.sidebar.collapsed .nav-item {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}

/* =========================================================
   Content
   ========================================================= */

.content {
  flex: 1;
  padding: 24px;
  min-width: 0;
  background: linear-gradient(180deg, rgba(255,255,255,.03), transparent);
}

/* =========================================================
   Backdrop / Modal
   ========================================================= */

/* 手機側欄遮罩：要在內容上，但在 sidebar 下 */
.backdrop {
  position: fixed;
  inset: var(--app-top-offset) 0 0 0;
  background: rgba(7,2,20,.62);
  z-index: 1080;              /* < sidebar(1090) */
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* Modal 遮罩：要蓋住一切 */
.modal-backdrop {
  position: fixed;
  inset: var(--app-top-offset) 0 0 0;
  background: rgba(7,2,20,.62);
  z-index: 1300;              /* 仍然最高 */
  display: grid;
  place-items: center;
  padding: 20px;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

/* 驗證 Modal */
.modal-card {
  width: 100%;
  max-width: 520px;
  padding: 20px 18px;
  border-radius: 18px;

  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.18);
  box-shadow: var(--shadow);

  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.modal-title {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}

.modal-text {
  margin: 0 0 14px;
  line-height: 1.6;
  color: rgba(255,255,255,.78);
}

.inline-link {
  color: rgba(167,139,250,.95);
  text-decoration: underline;
}

.modal-actions {
  display: flex;
  justify-content: center;
}

.btn.btn-primary {
  border: 0;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;

  color: #0b0616;
  background: linear-gradient(135deg, rgba(167,139,250,.95), rgba(96,165,250,.95));
  box-shadow: 0 12px 30px rgba(96,165,250,.18);
}

.btn.btn-primary:active {
  transform: translateY(1px);
}

/* =========================================================
   Announcement Modal
   ========================================================= */

.announcement-card {
  width: 100%;
  max-width: 720px;
  border-radius: 18px;
  overflow: hidden;
  position: relative;

  background: rgba(255,255,255,.10);
  border: 1px solid rgba(255,255,255,.16);
  box-shadow: var(--shadow);

  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
}

.announcement-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 34px;
  height: 34px;

  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.12);
  color: rgba(255,255,255,.92);

  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(0,0,0,.25);
}

.carousel-img,
.banner-img {
  width: 100%;
  display: block;
  object-fit: contain;
  background: rgba(0,0,0,.25);
}

/* =========================================================
   Carousel Controls
   ========================================================= */

.nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;

  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.12);
  color: rgba(255,255,255,.92);

  display: inline-flex;
  align-items: center;
  justify-content: center;

  box-shadow: 0 10px 22px rgba(0,0,0,.25);
  cursor: pointer;
}

.nav.prev { left: 10px; }
.nav.next { right: 10px; }

/* Dots */
.dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255,255,255,.25);
  border: 1px solid rgba(255,255,255,.25);
}

.dot.active {
  background: rgba(167,139,250,.9);
  border-color: rgba(167,139,250,.9);
}

/* =========================================================
   Mobile
   ========================================================= */

.menu-toggle { display: none; }

@media (max-width: 899px) {
  .app-shell { display: block; }
  .content { padding: 16px; }

  .menu-toggle {
    display: inline-flex;
    position: fixed;
    top: 60px;
    left: 0;

    width: 42px;
    height: 42px;
    border-radius: 0 14px 14px 0;

    border: 1px solid rgba(255,255,255,.16);
    background: rgba(255,255,255,.10);
    color: var(--accent);

    box-shadow: 0 12px 26px rgba(0,0,0,.28);
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);

    z-index: 1101;
  }

  .menu-toggle.open {
    left: clamp(0px, 80vw, 320px);
  }

  .sidebar {
    position: fixed;
    left: 0;
    top: var(--app-top-offset);
    width: 80vw;
    max-width: 320px;
    height: calc(100vh - var(--app-top-offset));

    transform: translateX(-100%);
    transition: transform .25s ease;
    z-index: 1090;

    background: rgba(255,255,255,.10);
    border-right: 1px solid rgba(255,255,255,.16);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
  }

  .sidebar.open {
    transform: translateX(0);
  }
}

/* Banner variant */
.announcement-card.has-banner { max-width: 450px; }
.announcement-card.has-banner .banner-img { max-height: 30vh; }
.announcement-card.has-banner .carousel-img { max-height: 50vh; }

</style>
